import { Paths as PathsV3_0, Paths as PathsV3_1 } from '@criteria/openapi/v3.1'
import {
  BreadcrumbItem,
  BreadcrumbLabel,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Heading,
  Input,
  Label,
  Legend,
  Select,
  Text,
  Textarea
} from '@criteria-labs/react'
import { Service } from '@criteria-labs/docs-kit'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { PageFormBody, PageFormFooter } from '../PageForm'
import { InspectorArticle, InspectorBody, InspectorHeader } from '../article'
import { AddEndpoint } from '../changes/endpoint'
import { Method, allMethods } from '../changes/operation'
import { useParams, useRouter } from '../router'
import { useDispatchChange } from '../useDispatchChange'
import { messsageFromError } from '../util/messageFromError'

type Inputs = {
  path: `/${string}`
  method: Method
  summary?: string
  description?: string
  resource?: string
}

export function NewEndpoint({ service }: { service: Service }) {
  const { push, back } = useRouter()

  const { resource_index } = useParams()
  const defaultResource = resource_index !== undefined ? service.resources[parseInt(resource_index)] : undefined

  const dispatchChange = useDispatchChange()
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    control,
    setValue
  } = useForm<Inputs>()
  const onSubmit: SubmitHandler<Inputs> = ({ path, method, summary, description, resource }) => {
    try {
      dispatchChange(
        new AddEndpoint(path, method, defaultResource?.tag.name ?? resource ?? null, { summary, description })
      )
      back()
    } catch (error) {
      setError('root', { message: messsageFromError(error) ?? 'Could not create endpoint' })
    }
  }

  const pathsObject = (service.openAPI.paths ?? {}) as PathsV3_0<never> | PathsV3_1<never>
  const existingPaths = Object.keys(pathsObject).filter((path) => path.startsWith('/'))

  const [pathSearch, setPathSearch] = useState('')
  const effectivePathSearch = pathSearch.startsWith('/') ? pathSearch : `/${pathSearch}`

  const matchingPaths =
    pathSearch === ''
      ? existingPaths
      : existingPaths.filter((path) => {
          return path.toLowerCase().includes(pathSearch.toLowerCase())
        })

  const watchedPath = watch('path')
  const pathItem = pathsObject[watchedPath as `/${string}`]
  let allowedMethods: Method[] | undefined = undefined
  if (pathItem) {
    allowedMethods = allMethods.filter((method) => !(method in pathItem))
  }

  return (
    <InspectorArticle>
      <InspectorHeader className="space-y-3">
        <div className="flex items-center justify-between">
          <BreadcrumbList className="min-h-10">
            <BreadcrumbItem onClick={() => push('/')}>
              <BreadcrumbLabel>API</BreadcrumbLabel>
            </BreadcrumbItem>
            {defaultResource && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem onClick={() => push(`/resources/${resource_index}`)}>
                  <BreadcrumbLabel>{defaultResource.tag.name ?? 'Resource'}</BreadcrumbLabel>
                </BreadcrumbItem>
              </>
            )}
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLabel>New Endpoint</BreadcrumbLabel>
            </BreadcrumbItem>
          </BreadcrumbList>
          <Button aria-label="Close" plain onClick={() => back()}>
            <XMarkIcon />
          </Button>
        </div>
        <Heading>Create a new endpoint</Heading>
        {defaultResource && (
          <Text>The endpoint will be associated with the &quot;{defaultResource.tag.name}&quot; resource.</Text>
        )}
      </InspectorHeader>
      <InspectorBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errors.root && <ErrorMessage className="mt-3">{errors.root.message}</ErrorMessage>}
          <PageFormBody className="space-y-8">
            <Fieldset>
              <Legend>HTTP details</Legend>
              <div className="mt-3 flex items-start gap-x-3">
                <div className="grow-0">
                  <Select {...register('method', { required: true })}>
                    {allMethods.map((method) => (
                      <option
                        key={method}
                        value={method}
                        disabled={allowedMethods ? !allowedMethods.includes(method) : false}
                      >
                        {method.toUpperCase()}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1">
                  <Field className="grow-1">
                    <Controller
                      name="path"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Combobox {...field} onChange={(value) => setValue('path', value as `/${string}`)}>
                          <ComboboxInput onChange={(event) => setPathSearch(event.target.value)} />
                          <ComboboxOptions>
                            {pathSearch.length > 0 && !matchingPaths.includes(effectivePathSearch) && (
                              <ComboboxOption value={effectivePathSearch}>
                                Create &quot;{effectivePathSearch}&quot;
                              </ComboboxOption>
                            )}
                            {matchingPaths.map((matchingPath) => (
                              <ComboboxOption key={matchingPath} value={matchingPath}>
                                {matchingPath}
                              </ComboboxOption>
                            ))}
                          </ComboboxOptions>
                        </Combobox>
                      )}
                    />
                  </Field>
                  {errors.path?.type === 'required' && <ErrorMessage className="mt-3">Path is required</ErrorMessage>}
                </div>
              </div>
            </Fieldset>
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Summary</Label>
                  <Input placeholder="Enter a summary" {...register('summary')} />
                </Field>
                <Field>
                  <Label>Description</Label>
                  <Textarea placeholder="Enter a description" {...register('description')} />
                </Field>
              </FieldGroup>
              {!defaultResource && (
                <FieldGroup>
                  <Field>
                    <Label>Add to resource</Label>
                    <Select {...register('resource')}>
                      <option value=""></option>
                      {service.resources.map((resource) => (
                        <option key={resource.slug} value={resource.tag.name}>
                          {resource.summary}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </FieldGroup>
              )}
            </Fieldset>
          </PageFormBody>
          <PageFormFooter>
            <Button type="submit" color="indigo">
              Create Endpoint
            </Button>
          </PageFormFooter>
        </form>
      </InspectorBody>
    </InspectorArticle>
  )
}
